import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, SafeAreaView, TouchableOpacity, Alert, ScrollView, ActivityIndicator, Platform } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Colors, SIZES } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import { locationService } from '../services/locationService';
import { callPickupFoodOrder, callGetOrderEta, callDeliverFoodOrder } from '../services/cloudFunctions';
import { volver } from '../utils/volver';
import { mensajeDeError } from '../utils/mensajeDeError';
import { pesos } from '../utils/dinero';

/** Los únicos estados en los que a este repartidor le queda algo por hacer. */
const ESTADOS_EN_CURSO = ['ASSIGNED', 'PICKED_UP'];
const ESTADOS_TERMINADOS = ['CANCELLED', 'DELIVERED'];
/** Plazo para que llegue el pedido antes de admitir que no cargó. */
const GRACIA_CARGA_MS = 15_000;

export default function DeliveryActiveScreen() {
  const router = useRouter();
  const { orderId } = useLocalSearchParams<{ orderId: string }>();
  const miUid = useAppStore((s) => s.userFirebaseUid);
  const [pin, setPin] = useState('');
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutos = 600 segundos
  // H26, auditoría 16-sep-2026 — EL RELOJ DE ESPERA ARRANCABA AL ABRIR LA
  // PANTALLA. `timeLeft` nacía en 600 al montar, sin mirar estado ni posición:
  // a los 10 minutos de abrirla —aunque el repartidor todavía fuera MANEJANDO
  // hacia el restaurante— saltaba «Tiempo de espera agotado» invitándolo a
  // devolver un pedido que ni había recogido. Ahora el reloj mide lo que dice
  // medir: la espera en la puerta, desde que él toca «Ya llegué donde el cliente».
  const [esperaIniciada, setEsperaIniciada] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  // realPin EMPIEZA EN null y solo se establece si Firestore devuelve el pin del pedido.
  // Sin esto el numpad rechazará cualquier intento — antes había un fallback '8492'
  // que volvía a la pantalla insegura si la red fallaba.
  const [realPin, setRealPin] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<any>(null);
  const [loadingPin, setLoadingPin] = useState(true);
  // H67, auditoría 16-sep-2026 — el `null` del listener (pedido que ya no se
  // puede leer: reasignado, permiso, id malo) se tiraba: la pantalla seguía con
  // los datos viejos o con 'Cliente' / 'Dirección no informada' y el numpad
  // activo. Ahora se apunta y la pantalla lo dice.
  const [pedidoIlegible, setPedidoIlegible] = useState(false);
  const [cargaVencida, setCargaVencida] = useState(false);

  // Suscripción en vivo al pedido para reflejar cambios (PICKED_UP, etc.)
  // SEGURIDAD — Ya NO leemos `securityPin` del doc (las reglas Firestore lo
  // bloquean al repartidor). El PIN se verifica SOLO en el servidor vía
  // callDeliverFoodOrder. `realPin` queda deprecado.
  useEffect(() => {
    if (!orderId) {
      setLoadingPin(false);
      return;
    }
    const unsub = firestoreService.listenToFoodOrderById(orderId, (order) => {
      if (order) {
        setOrderData(order);
        setPedidoIlegible(false);
      } else {
        setPedidoIlegible(true);
      }
      setLoadingPin(false);
    });
    return () => unsub();
  }, [orderId]);

  useEffect(() => {
    if (!loadingPin) return;
    const id = setTimeout(() => setCargaVencida(true), GRACIA_CARGA_MS);
    return () => clearTimeout(id);
  }, [loadingPin, orderId]);

  // Un pedido CANCELADO mientras iba en camino, ya ENTREGADO desde otra sesión,
  // o que ya no es de este repartidor caía en la rama «ENTREGAR AL CLIENTE» con
  // el numpad activo: cruzaba la ciudad por un pedido muerto y al teclear el PIN
  // recibía el error crudo del servidor. Esto decide cuándo ya no hay entrega.
  const yaNoEsMio = !!orderData?.driverId && !!miUid && orderData.driverId !== miUid;
  // Terminado es SOLO lo que ya no tiene vuelta. Un READY/PREPARING/PENDING justo
  // después de tomar el pedido suele ser la caché que llega antes que el
  // ASSIGNED del servidor: se espera un poco en vez de mandarlo fuera.
  const pedidoTerminado =
    !!orderData && (ESTADOS_TERMINADOS.includes(String(orderData.status)) || yaNoEsMio);
  const esperandoAsignacion =
    !!orderData && !pedidoTerminado && !ESTADOS_EN_CURSO.includes(String(orderData.status));
  const [asignacionVencida, setAsignacionVencida] = useState(false);
  useEffect(() => {
    if (!esperandoAsignacion) {
      setAsignacionVencida(false);
      return;
    }
    const id = setTimeout(() => setAsignacionVencida(true), GRACIA_CARGA_MS);
    return () => clearTimeout(id);
  }, [esperandoAsignacion]);

  // H15 — el teléfono del cliente ya no viene en el pedido (lo leía todo el feed
  // READY). Vive en `private/contacto`, y la regla solo lo abre al repartidor
  // YA asignado: se escucha desde que el pedido es suyo.
  const [telefonoCliente, setTelefonoCliente] = useState<string | null>(null);
  const [direccionCliente, setDireccionCliente] = useState<string | null>(null);
  const esMiPedido = !!orderId && !!miUid && orderData?.driverId === miUid;
  useEffect(() => {
    if (!esMiPedido || !orderId) return;
    return firestoreService.escucharPrivadoDelPedido(orderId, 'contacto', (data) => {
      setTelefonoCliente(String(data?.customerPhone ?? '').trim() || null);
      // V21 — la dirección exacta viaja por el mismo documento privado y por el
      // mismo motivo: solo la ve el repartidor que YA tiene el pedido asignado.
      setDireccionCliente(String(data?.deliveryAddress ?? '').trim() || null);
    });
  }, [esMiPedido, orderId]);
  const telefonoMostrado = telefonoCliente ?? (String(orderData?.customerPhone ?? '').trim() || null);
  const direccionMostrada =
    direccionCliente ?? (String(orderData?.deliveryAddress ?? '').trim() || null);

  // V17 — EL REPARTIDOR QUE NO SE «CONECTÓ» NO EMITÍA GPS. El feed se abre y
  // `claimFoodOrder` acepta sin estar conectado, pero `setTrackingMode` no hace
  // nada si no hay vigilante (solo lo arranca «Conectarse» en el mapa): el
  // cliente no lo veía nunca y la ETA usaba una posición vieja. Con la entrega
  // en curso y sin rastreo vivo se enciende aquí, igual que el mapa al reanudar
  // un viaje (startTracking en ACTIVE_RIDE; solo el permiso de «mientras se
  // usa»). Cubre el salto tras tomar el pedido y la vuelta tras cerrar la app.
  // Lo que se encendió aquí se apaga aquí al terminar; al que ya estaba
  // conectado solo se le baja a IDLE, como antes.
  const gpsEncendidoAquiRef = useRef(false);
  const entregaMiaEnCurso = esMiPedido && ESTADOS_EN_CURSO.includes(String(orderData?.status ?? ''));
  useEffect(() => {
    if (!entregaMiaEnCurso || !miUid || Platform.OS === 'web') return;
    if (locationService.modoDeRastreoActual() !== null) return;
    (async () => {
      try {
        await locationService.startTracking(miUid, 'ACTIVE_RIDE');
        if (locationService.modoDeRastreoActual() !== null) gpsEncendidoAquiRef.current = true;
      } catch (e: any) {
        console.warn('[delivery-active] no se pudo encender el GPS de la entrega:', e?.message);
      }
    })();
  }, [entregaMiaEnCurso, miUid]);

  const bajarGpsDeLaEntrega = () => {
    if (gpsEncendidoAquiRef.current) {
      gpsEncendidoAquiRef.current = false;
      locationService.stopTracking().catch(() => {});
    } else {
      locationService.setTrackingMode('IDLE').catch(() => {});
    }
  };

  // Cancelado o entregado con la pantalla abierta: el GPS encendido para esta
  // entrega no se queda emitiendo.
  useEffect(() => {
    if (pedidoTerminado && gpsEncendidoAquiRef.current) bajarGpsDeLaEntrega();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidoTerminado]);

  // ETA refresh
  const [etaMinutes, setEtaMinutes] = useState<number | null>(null);
  useEffect(() => {
    if (!orderId) return;
    let stopped = false;
    const tick = async () => {
      try {
        const r = await callGetOrderEta({ orderId });
        if (!stopped) setEtaMinutes(r.data.etaMinutes);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { stopped = true; clearInterval(id); };
  }, [orderId]);

  // H43, auditoría 16-sep-2026 — «Ya recogí el pedido» no tenía candado: el
  // segundo toque llegaba con el pedido ya PICKED_UP y el repartidor leía
  // «Error: Status inválido: PICKED_UP» con la comida en la mano. Mismo patrón
  // que `enviandoReporte` más abajo: ref para cortar el toque doble en el acto
  // y estado para apagar el botón.
  const [marcandoRecogida, setMarcandoRecogida] = useState(false);
  const marcandoRecogidaRef = useRef(false);
  const handleMarkPickedUp = async () => {
    if (!orderId || marcandoRecogidaRef.current) return;
    marcandoRecogidaRef.current = true;
    setMarcandoRecogida(true);
    try {
      await callPickupFoodOrder({ orderId });
    } catch (e: any) {
      const msg = String(e?.message ?? '');
      // Si el servidor dice que YA está recogido, el pedido está justo como el
      // repartidor quería: no es un error que haya que enseñarle. (El servidor
      // nuevo ya responde ok en ese caso; esto cubre al que aún no se desplegó.)
      if (msg.includes('PICKED_UP')) return;
      // Sin permiso = «No eres el repartidor asignado», redactado por el servidor.
      Alert.alert('No pudimos marcar la recogida', String(e?.code ?? '').includes('permission-denied') && msg ? msg : mensajeDeError(e, 'Revisa tu conexión e inténtalo otra vez.'));
    } finally {
      marcandoRecogidaRef.current = false;
      setMarcandoRecogida(false);
    }
  };

  useEffect(() => {
    if (!esperaIniciada || pedidoTerminado) return;
    if (timeLeft <= 0) {
      if (!isReturning) {
        Alert.alert(
          'Tiempo de Espera Agotado',
          // ── NI GPS NI PAGO GARANTIZADO ────────────────────────────────
          // Este aviso no valida ninguna coordenada (solo pasaron 10 minutos
          // desde que el repartidor tocó «Ya llegué») y Go Safely no garantiza
          // ni abona nada: el cobro va directo del cliente al repartidor
          // (Términos, puntos 2 y 8).
          //
          // H26 — Antes el botón destructivo era «Iniciar Devolución», que
          // llevaba a un numpad que aceptaba CUALQUIER PIN y no registraba
          // nada: el pedido se quedaba abierto para siempre y el repartidor
          // creía haberlo cerrado. Y «Esperar 5 min más» no esperaba: el reloj
          // se quedaba en 0 y el aviso no volvía. Ahora las dos hacen lo que
          // dicen.
          'Pasaron los 10 minutos de espera. Puedes esperar 5 minutos más o, si el cliente no aparece, ver qué hacer con el pedido.',
          [
            { text: 'Esperar 5 min más', style: 'cancel', onPress: () => setTimeLeft(300) },
            { text: 'El cliente no aparece', style: 'destructive', onPress: () => setIsReturning(true) }
          ]
        );
      }
      return;
    }
    const timerId = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(timerId);
  }, [timeLeft, isReturning, esperaIniciada, pedidoTerminado]);

  const iniciarEspera = () => {
    setTimeLeft(600);
    setEsperaIniciada(true);
  };

  // El cliente apareció: se sale del modo «no aparece» y se apaga el reloj (si
  // quedara en 0 con la espera activa, el aviso saltaría otra vez en el acto).
  const clienteLlego = () => {
    setIsReturning(false);
    setEsperaIniciada(false);
    setTimeLeft(600);
    setPin('');
  };

  // Salida de las pantallas sin entrega (cancelado, entregado, ya no es suyo,
  // no cargó). Baja el GPS a IDLE, igual que al entregar: ya no hay nadie a
  // quien le sirva la posición en alta precisión.
  const volverALosPedidos = () => {
    // Solo si de verdad ya no hay entrega: si el pedido aún podía ser suyo (no
    // cargó, o la asignación no se confirmó), bajarle el GPS lo dejaría con la
    // ubicación lenta en plena entrega.
    if (pedidoTerminado) bajarGpsDeLaEntrega();
    volver(router, '/deliveries-feed');
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleKeyPress = (key: string) => {
    if (pin.length < 4) {
      const newPin = pin + key;
      setPin(newPin);
      
      if (newPin.length === 4) {
        verifyPin(newPin);
      }
    }
  };

  const handleDelete = () => {
    setPin(pin.slice(0, -1));
  };

  const [verifying, setVerifying] = useState(false);

  // REPORTES DE SEGURIDAD DEL REPARTIDOR
  //
  // Antes estos dos botones solo abrían un Alert que decía "Se ha notificado a
  // seguridad". No se notificaba a nadie: no salía una sola escritura de la
  // app. En una plataforma cuyo nombre es "Go Safely", prometerle a un
  // repartidor que avisamos a seguridad cuando no avisamos a nadie es lo peor
  // que puede hacer este código.
  //
  // Ahora la alerta queda registrada en `sos_alerts` — la misma bandeja del
  // botón de pánico, marcada con su `tipo` para poder priorizar.
  const [enviandoReporte, setEnviandoReporte] = useState(false);
  const reportarSeguridad = async (
    tipo: 'ZONA_PELIGROSA' | 'ESPERA_EXCEDIDA',
    tituloOk: string,
    mensajeOk: string
  ) => {
    if (enviandoReporte) return;
    setEnviandoReporte(true);
    try {
      const s = useAppStore.getState();
      const pos = await locationService.getCurrentLocation().catch(() => null);
      await firestoreService.registrarSOS({
        tipo,
        uid: s.userFirebaseUid ?? null,
        nombre: s.userName ?? null,
        telefono: s.userPhone ?? null,
        lat: pos?.coords?.latitude ?? null,
        lng: pos?.coords?.longitude ?? null,
        rideId: orderId ?? null,
        rol: s.userRole ?? null,
      });
      Alert.alert(tituloOk, mensajeOk);
    } catch (e: any) {
      console.warn('[reportarSeguridad]', e?.code, e?.message);
      // Decirle "listo" cuando falló sería repetir el problema original.
      Alert.alert(
        'No pudimos enviar el reporte',
        `Revisa tu conexión e intenta de nuevo. Si estás en peligro, llama al 911 ahora mismo.`
      );
    } finally {
      setEnviandoReporte(false);
    }
  };
  const verifyPin = async (enteredPin: string) => {
    // FIX SEGURIDAD CRÍTICO — El PIN se verifica EN EL SERVIDOR. Antes se
    // comparaba en el cliente y se escribía DELIVERED directo, lo que permitía
    // a un repartidor malicioso liberar el pago sin el cliente. Ahora llamamos
    // a la Cloud Function que compara el PIN server-side y solo ahí marca
    // DELIVERED + libera el pago.
    // H26, auditoría 16-sep-2026 — AQUÍ ESTABA EL TEATRO. En «devolución»
    // cualquier PIN de 4 dígitos mandaba al repartidor al mapa sin comparar
    // nada ni escribir nada: el pedido seguía ASSIGNED/PICKED_UP para siempre y
    // él creía haberlo cerrado. No existe ninguna función de servidor que
    // registre una devolución, así que en ese modo el numpad ya no se muestra y
    // esto no hace nada; la pantalla le dice la verdad (chat, soporte).
    if (isReturning) return;
    if (!orderId || pedidoTerminado) return;
    setVerifying(true);
    try {
      await callDeliverFoodOrder({ orderId, pin: enteredPin });
      bajarGpsDeLaEntrega();
      Alert.alert(
        '¡Entrega Confirmada!',
        // "El pago se libera a tu cuenta" no describe nada: `deliverFoodOrder`
        // (functions/src/foodDispatch.ts) solo marca el pedido DELIVERED y
        // avisa al cliente — no abona un peso a ninguna billetera. El cobro es
        // directo entre cliente y repartidor, en efectivo o transferencia
        // (Términos, puntos 2 y 8).
        'El PIN fue verificado y la entrega quedó registrada. Cóbrale el pedido al cliente en efectivo o por transferencia antes de irte.',
        [{ text: 'Continuar', onPress: () => router.replace('/(tabs)/map') }]
      );
    } catch (e: any) {
      const code = String(e?.code ?? '');
      const msg = String(e?.message ?? '');
      let human = 'El PIN ingresado no es válido. Pídeselo de nuevo al cliente.';
      if (code.includes('resource-exhausted') || msg.includes('intentos')) {
        human = 'Demasiados intentos de PIN. Contacta al cliente o a soporte.';
      } else if (code.includes('permission-denied') && !msg.toLowerCase().includes('pin')) {
        human = 'No eres el repartidor asignado a este pedido.';
      } else if (msg) {
        // El «PIN incorrecto» llega como permission-denied, redactado por el servidor.
        human = code.includes('permission-denied') ? msg : mensajeDeError(e, human);
      }
      Alert.alert('Código Incorrecto', human);
      setPin('');
    } finally {
      setVerifying(false);
    }
  };

  // ── H67 — PANTALLAS SIN ENTREGA ──────────────────────────────────────────
  // `loadingPin` se apuntaba pero el render nunca lo miraba: ni spinner ni
  // error. Estas tres salidas van ANTES del numpad para que nunca se pida un PIN
  // de un pedido que no cargó, que se canceló o que ya no es de este repartidor.
  if ((!orderData && !!orderId && loadingPin && !cargaVencida) || (esperandoAsignacion && !asignacionVencida && !pedidoIlegible)) {
    return (
      <SafeAreaView style={[styles.safeArea, styles.pantallaCentrada]}>
        <ActivityIndicator size="large" color={Colors.primary} />
        <Text style={styles.cargandoTexto}>
          {esperandoAsignacion ? 'Confirmando que el pedido es tuyo…' : 'Cargando el pedido…'}
        </Text>
      </SafeAreaView>
    );
  }

  if (!orderData || pedidoIlegible || pedidoTerminado || esperandoAsignacion) {
    let icono: any = 'alert-circle-outline';
    let titulo = 'No pudimos cargar este pedido';
    let detalle =
      'Puede que ya no esté asignado a ti o que se haya caído la conexión. Vuelve a la lista de pedidos: si sigue siendo tuyo, aparece arriba como entrega en curso.';
    if (orderData && !pedidoIlegible && orderData.status === 'CANCELLED') {
      icono = 'close-circle-outline';
      titulo = 'Este pedido fue cancelado';
      detalle =
        (orderData.cierreAutomatico === 'SIN_RECOGER'
          ? 'Se cerró solo porque pasaron 90 minutos sin marcarlo como recogido. '
          : '') +
        'No tienes que recogerlo ni entregarlo, y no hay nada que cobrar.';
    } else if (orderData && !pedidoIlegible && orderData.status === 'DELIVERED' && !yaNoEsMio) {
      icono = 'checkmark-circle-outline';
      titulo = 'Este pedido ya fue entregado';
      // Esta pantalla sale también justo después de SU entrega (el pedido pasa
      // a DELIVERED al instante), así que no puede esconderle el cobro: Go
      // Safely no cobra por él, el dinero lo recibe del cliente en la puerta.
      const monto = Number(orderData.total ?? 0);
      if (orderData.paymentMethod === 'CASH' && monto > 0) {
        detalle = `La entrega quedó registrada. Si todavía no cobraste, cóbrale ${pesos(monto)} en efectivo al cliente.`;
      } else if (orderData.paymentMethod === 'TRANSFER' && monto > 0) {
        detalle = `La entrega quedó registrada. Si todavía no lo confirmaste, verifica con el cliente la transferencia de ${pesos(monto)}.`;
      } else {
        detalle = 'La entrega ya quedó registrada. No tienes que hacer nada más con este pedido.';
      }
    } else if (esperandoAsignacion && !pedidoIlegible) {
      titulo = 'Este pedido todavía no figura a tu nombre';
      detalle = 'No lo recojas todavía. Vuelve a la lista de pedidos: si quedó asignado a ti, aparece arriba como entrega en curso.';
    } else if (orderData && !pedidoIlegible) {
      titulo = 'Este pedido ya no está asignado a ti';
      detalle = 'No lo recojas, no lo entregues y no lo cobres. Vuelve a la lista para tomar otro.';
    } else if (!pedidoIlegible && cargaVencida && orderId) {
      detalle =
        'La conexión está tardando más de lo normal. Seguimos intentándolo: si el pedido llega, esta pantalla se actualiza sola.';
    }
    return (
      <SafeAreaView style={[styles.safeArea, styles.pantallaCentrada]}>
        <Ionicons name={icono} size={56} color={Colors.textSecondary} />
        <Text style={styles.sinPedidoTitulo}>{titulo}</Text>
        <Text style={styles.sinPedidoDetalle}>{detalle}</Text>
        <TouchableOpacity style={styles.pickupBtn} onPress={volverALosPedidos}>
          <Ionicons name="arrow-back" size={18} color="#FFF" />
          <Text style={styles.pickupBtnText}>Volver a los pedidos</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView contentContainerStyle={{ flexGrow: 1 }} bounces={false}>
        {/* HEADER DE ESTADO */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            {isReturning ? 'CLIENTE NO APARECE' : 'ENTREGA EN CURSO'}
          </Text>
          {/* El reloj solo se ve cuando de verdad está midiendo la espera en la
              puerta (H26). Antes contaba desde que se abría la pantalla. */}
          {esperaIniciada && (
            <View style={styles.timerBadge}>
              <Ionicons name="time-outline" size={16} color="#FFF" style={{marginRight: 4}} />
              <Text style={styles.timerText}>
                {timeLeft > 0 ? formatTime(timeLeft) : '0:00'}
              </Text>
            </View>
          )}
        </View>

        {/* INFO DE LA ENTREGA — datos reales del order */}
        {!isReturning && (
          <View style={styles.previewContainer}>
            {orderData?.status === 'ASSIGNED' ? (
              <>
                <View style={[styles.previewTag, { backgroundColor: '#0066CC' }]}>
                  <Ionicons name="storefront" size={14} color="#FFF" />
                  <Text style={styles.previewTagText}>RECOGER EN RESTAURANTE</Text>
                </View>
                <View style={styles.infoBox}>
                  <Text style={styles.infoTitle}>{orderData?.restaurantName ?? 'Restaurante'}</Text>
                  <Text style={styles.infoLine}>📦 Items: {orderData?.items ?? '—'}</Text>
                  {etaMinutes !== null && (
                    <Text style={styles.infoLine}>⏱️ Llegada estimada: {etaMinutes} min</Text>
                  )}
                </View>
                <TouchableOpacity
                  style={[styles.pickupBtn, marcandoRecogida && { opacity: 0.6 }]}
                  onPress={handleMarkPickedUp}
                  disabled={marcandoRecogida}
                >
                  {marcandoRecogida ? (
                    <ActivityIndicator size="small" color="#FFF" />
                  ) : (
                    <Ionicons name="checkmark-circle" size={20} color="#FFF" />
                  )}
                  <Text style={styles.pickupBtnText}>
                    {marcandoRecogida ? 'Marcando…' : 'Ya recogí el pedido'}
                  </Text>
                </TouchableOpacity>
              </>
            ) : (
              <>
                <View style={styles.previewTag}>
                  <Ionicons name="home" size={14} color="#FFF" />
                  <Text style={styles.previewTagText}>ENTREGAR AL CLIENTE</Text>
                </View>
                <View style={styles.infoBox}>
                  <Text style={styles.infoTitle}>{orderData?.customerName ?? 'Cliente'}</Text>
                  <Text style={styles.infoLine}>
                    📍 {direccionMostrada ?? orderData?.zonaEntrega ?? 'Dirección no informada'}
                  </Text>
                  {!!telefonoMostrado && (
                    <Text style={styles.infoLine}>📞 {telefonoMostrado}</Text>
                  )}
                  {etaMinutes !== null && (
                    <Text style={styles.infoLine}>⏱️ Llegada estimada: {etaMinutes} min</Text>
                  )}
                </View>
                {/* H26 — el reloj de espera arranca AQUÍ, en la puerta. */}
                {!esperaIniciada && (
                  <TouchableOpacity style={styles.pickupBtn} onPress={iniciarEspera}>
                    <Ionicons name="location" size={20} color="#FFF" />
                    <Text style={styles.pickupBtnText}>Ya llegué donde el cliente</Text>
                  </TouchableOpacity>
                )}
                {/* Salida visible aunque el aviso no se vea (en web `Alert` no
                    dibuja nada) o se haya cerrado. Solo tras los 10 minutos. */}
                {esperaIniciada && timeLeft <= 0 && (
                  <TouchableOpacity
                    style={[styles.pickupBtn, { backgroundColor: '#FF3B30' }]}
                    onPress={() => setIsReturning(true)}
                  >
                    <Ionicons name="alert-circle" size={20} color="#FFF" />
                    <Text style={styles.pickupBtnText}>El cliente no aparece</Text>
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>
        )}

        {isReturning && (
          <View style={styles.previewContainer}>
            <View style={[styles.previewTag, { backgroundColor: '#FF3B30' }]}>
              <Ionicons name="alert-circle" size={14} color="#FFF" />
              <Text style={styles.previewTagText}>CLIENTE NO APARECE</Text>
            </View>
            <View style={{ width: '100%', minHeight: 180, borderRadius: 12, backgroundColor: '#FFEBEB', justifyContent: 'center', alignItems: 'center', paddingVertical: 16 }}>
              <Ionicons name="alert-circle" size={48} color="#FF3B30" />
              {/* ── H26, auditoría 16-sep-2026: SE ACABÓ LA «DEVOLUCIÓN» ────
                  Esta tarjeta decía «Devolución al remitente» y mandaba a pedirle
                  un PIN que no existe (el «remitente» de un pedido de comida es
                  el restaurante, y no tiene PIN) para «cerrar el viaje». Debajo,
                  el numpad aceptaba CUALQUIER número y no escribía nada: el
                  pedido seguía abierto y el repartidor creía haberlo cerrado.
                  Tampoco era cierto lo de cobrarle el viaje al restaurante: no lo
                  dice ningún término ni lo registra ninguna función.
                  Ninguna función de servidor registra devoluciones, así que se
                  dice eso y se dan las salidas que SÍ existen: el chat con el
                  cliente, el reporte a soporte (queda en `sos_alerts`, que el
                  panel revisa) y soporte, que es quien puede cerrar el pedido
                  con `cancelFoodOrder`. Sigue sin haber pago garantizado ni
                  sistema de «faltas» (Términos, puntos 8 y 9). */}
              <Text style={{color: '#333', marginTop: 10, fontWeight: 'bold', textAlign: 'center', paddingHorizontal: 20}}>
                La app no registra devoluciones.
              </Text>
              <Text style={{color: '#FF3B30', marginTop: 5, textAlign: 'center', paddingHorizontal: 20, fontSize: 12}}>
                No te vayas con el pedido sin avisar. Escríbele al cliente por el chat. Si no responde, toca «Avisar a soporte»: queda registrado con tu ubicación, y soporte revisa el caso y es quien puede cerrar el pedido. Si el cliente aparece, toca «El cliente llegó» y pídele su PIN.
              </Text>
            </View>

            {/* BOTONES DE REPORTE */}
            <View style={{flexDirection: 'row', justifyContent: 'space-between', marginTop: 15, gap: 10}}>
              <TouchableOpacity
                style={{flex: 1, backgroundColor: '#FFF0F0', borderWidth: 1, borderColor: '#FF3B30', borderRadius: 8, padding: 10, alignItems: 'center', opacity: enviandoReporte ? 0.5 : 1}}
                disabled={enviandoReporte}
                onPress={() => reportarSeguridad(
                  'ZONA_PELIGROSA',
                  'Reporte enviado',
                  'Registramos la alerta con tu ubicación y nuestro equipo la va a revisar. Si estás en peligro inmediato, llama al 911 y abandona el área.'
                )}
              >
                <Ionicons name="warning" size={20} color="#FF3B30" />
                <Text style={{color: '#FF3B30', fontSize: 12, fontWeight: 'bold', marginTop: 4, textAlign: 'center'}}>Zona Peligrosa</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{flex: 1, backgroundColor: '#F0F4FF', borderWidth: 1, borderColor: Colors.primary, borderRadius: 8, padding: 10, alignItems: 'center', opacity: enviandoReporte ? 0.5 : 1}}
                disabled={enviandoReporte}
                onPress={() => reportarSeguridad(
                  'ESPERA_EXCEDIDA',
                  'Reporte enviado',
                  // El reporte SÍ se registra (`registrarSOS` escribe en
                  // `sos_alerts`), y eso es lo que se dice. Nada de "tu pago
                  // está garantizado" (Términos, punto 8) ni de "devolver el
                  // paquete y cobrarle al remitente" (H26): no existe ese flujo.
                  'Quedó registrado que el cliente no apareció, con tu ubicación. Soporte revisa el caso. Mientras tanto no des el pedido por entregado.'
                )}
              >
                <Ionicons name="time" size={20} color={Colors.primary} />
                <Text style={{color: Colors.primary, fontSize: 12, fontWeight: 'bold', marginTop: 4, textAlign: 'center'}}>Avisar a soporte</Text>
              </TouchableOpacity>
            </View>

            <View style={{flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, gap: 10}}>
              <TouchableOpacity
                style={{flex: 1, backgroundColor: '#F0F4FF', borderWidth: 1, borderColor: Colors.primary, borderRadius: 8, padding: 10, alignItems: 'center'}}
                onPress={() => router.push(`/order-chat?orderId=${orderId}` as any)}
              >
                <Ionicons name="chatbubble-outline" size={20} color={Colors.primary} />
                <Text style={{color: Colors.primary, fontSize: 12, fontWeight: 'bold', marginTop: 4, textAlign: 'center'}}>Chatear con el cliente</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={{flex: 1, backgroundColor: '#E8F5E9', borderWidth: 1, borderColor: '#4CAF50', borderRadius: 8, padding: 10, alignItems: 'center'}}
                onPress={clienteLlego}
              >
                <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
                <Text style={{color: '#4CAF50', fontSize: 12, fontWeight: 'bold', marginTop: 4, textAlign: 'center'}}>El cliente llegó</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {/* ── "YA PAGADO" NO LO SABE NADIE ────────────────────────────────
            El pedido por transferencia nace con `paymentStatus:
            'NOT_APPLICABLE'` (functions/src/orders.ts:340) y NADA lo cambia
            nunca: no hay un solo sitio que confirme que el dinero entró. Lo
            único que puede existir es `receiptImage`, la foto que sube el
            propio cliente — y una foto no es un depósito acreditado. El
            repartidor leía "YA PAGADO", entregaba y no cobraba. Ahora el
            cintillo dice lo que hay que hacer, no un hecho que nadie
            comprobó. */}
        {/* MONTO A COBRAR — visible grande para evitar reclamos del cliente */}
        {!isReturning && orderData && (orderData.paymentMethod === 'CASH' || orderData.paymentMethod === 'TRANSFER') && (
          <View style={styles.amountBanner}>
            <View style={{ flex: 1 }}>
              <Text style={styles.amountLabel}>
                {orderData.paymentMethod === 'CASH' ? '💵 COBRA EN EFECTIVO' : '🏦 PAGO POR TRANSFERENCIA'}
              </Text>
              <Text style={styles.amountValue}>{pesos(orderData.total ?? 0)}</Text>
              {orderData.tip > 0 && (
                <Text style={styles.amountTip}>incluye {pesos(orderData.tip)} de propina para ti</Text>
              )}
            </View>
            {orderData.paymentMethod === 'TRANSFER' && (
              <View style={styles.transferBadge}>
                <Ionicons name="checkmark-circle" size={14} color="#FFF" />
                <Text style={styles.transferBadgeText}>REVISA EL COMPROBANTE</Text>
              </View>
            )}
          </View>
        )}

        {/* SISTEMA DE BLOQUEO (NUMPAD) — solo cuando hay una entrega que
            registrar. En «cliente no aparece» no se muestra: ahí no hay PIN que
            valga (H26). */}
        {!isReturning && (
        <View style={styles.lockSystem}>
          <View style={styles.lockHeader}>
            <Ionicons name={pin.length === 4 ? "lock-open" : "lock-closed"} size={32} color={pin.length === 4 ? "#4CAF50" : Colors.accent} />
            <Text style={styles.lockTitle}>Paquete Bloqueado</Text>
            {/* Decía «al destinatario (o remitente) para liberar el paquete y
                recibir tu pago»: el PIN no libera ningún pago (el cobro es
                directo del cliente, Términos punto 8) y el «remitente» no tiene
                PIN. Lo que hace de verdad es registrar la entrega. */}
            <Text style={styles.lockSubtitle}>
              Pídele al cliente su <Text style={{fontWeight: 'bold', color: Colors.text}}>PIN de 4 dígitos</Text> para registrar la entrega.
            </Text>
          </View>

          {/* PIN INDICATORS */}
          <View style={styles.pinDotsContainer}>
            {[0, 1, 2, 3].map((i) => (
              <View 
                key={i} 
                style={[
                  styles.pinDot, 
                  pin.length > i && styles.pinDotFilled,
                  pin.length > i && pin.length < 4 && { borderColor: Colors.accent }
                ]} 
              >
                {pin.length > i && <Text style={styles.pinDotText}>{pin[i]}</Text>}
              </View>
            ))}
          </View>

          {/* KEYPAD */}
          <View style={styles.keypad}>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((key) => (
              <TouchableOpacity key={key} style={styles.key} onPress={() => handleKeyPress(key)}>
                <Text style={styles.keyText}>{key}</Text>
              </TouchableOpacity>
            ))}
            <View style={styles.keyEmpty} />
            <TouchableOpacity style={styles.key} onPress={() => handleKeyPress('0')}>
              <Text style={styles.keyText}>0</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.key} onPress={handleDelete}>
              <Ionicons name="backspace-outline" size={28} color={Colors.text} />
            </TouchableOpacity>
          </View>
        </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  // H67 — carga / pedido que ya no se entrega
  pantallaCentrada: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  cargandoTexto: {
    marginTop: 12,
    fontSize: 13,
    color: Colors.textSecondary,
  },
  sinPedidoTitulo: {
    marginTop: 12,
    fontSize: 17,
    fontWeight: '800',
    color: Colors.text,
    textAlign: 'center',
  },
  sinPedidoDetalle: {
    marginTop: 6,
    marginBottom: 8,
    fontSize: 13,
    color: Colors.textSecondary,
    textAlign: 'center',
    lineHeight: 18,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#1E1E1E',
  },
  headerTitle: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: 1,
  },
  timerBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 59, 48, 0.2)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#FF3B30',
  },
  timerText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 14,
  },
  previewContainer: {
    backgroundColor: '#FFF',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#EEE',
  },
  previewTag: {
    position: 'absolute',
    top: 25,
    left: 25,
    zIndex: 10,
    backgroundColor: '#00C853',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  previewTagText: {
    color: '#FFF',
    fontSize: 10,
    fontWeight: 'bold',
    marginLeft: 4,
  },
  infoBox: {
    backgroundColor: '#F8F9FA',
    padding: 14,
    borderRadius: 12,
    marginTop: 10,
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text,
    marginBottom: 6,
  },
  infoLine: {
    fontSize: 13,
    color: Colors.textSecondary,
    marginTop: 4,
  },
  pickupBtn: {
    backgroundColor: '#0066CC',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 10,
    marginTop: 12,
  },
  pickupBtnText: {
    color: '#FFF',
    fontWeight: '700',
    marginLeft: 8,
  },
  amountBanner: {
    backgroundColor: '#1B5E20',
    margin: 12,
    padding: 18,
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.15,
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 8,
    elevation: 6,
  },
  amountLabel: {
    color: '#A5D6A7',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
  },
  amountValue: {
    color: '#FFF',
    fontSize: 36,
    fontWeight: '900',
    marginTop: 4,
    letterSpacing: -0.5,
  },
  amountTip: {
    color: '#A5D6A7',
    fontSize: 11,
    marginTop: 4,
    fontStyle: 'italic',
  },
  transferBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#2E7D32',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
  },
  transferBadgeText: {
    color: '#FFF',
    fontSize: 10,
    fontWeight: '800',
    marginLeft: 4,
    letterSpacing: 0.5,
  },
  lockSystem: {
    flex: 1,
    backgroundColor: '#FFF',
    marginTop: 10,
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    paddingTop: 30,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: -5 },
    elevation: 10,
  },
  lockHeader: {
    alignItems: 'center',
    paddingHorizontal: 30,
  },
  lockTitle: {
    fontSize: 22,
    fontWeight: '900',
    color: Colors.text,
    marginTop: 10,
  },
  lockSubtitle: {
    fontSize: 14,
    color: Colors.textSecondary,
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 20,
  },
  pinDotsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: 30,
    gap: 15,
  },
  pinDot: {
    width: 50,
    height: 60,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#E0E0E0',
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
  },
  pinDotFilled: {
    borderColor: '#4CAF50',
    backgroundColor: '#E8F5E9',
  },
  pinDotText: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
  },
  keypad: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: 280,
    gap: 15,
    marginBottom: 40,
  },
  key: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#F5F5F5',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  keyEmpty: {
    width: 80,
    height: 80,
  },
  keyText: {
    fontSize: 32,
    color: Colors.text,
    fontWeight: '500',
  }
});
